import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Mail,
  Phone,
  Building2,
  Globe,
  Clock,
  MessageSquare,
  Tag,
  CheckCircle2,
  XCircle,
  ChevronDown,
  Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';
import SourceLink from '../components/SourceLink';
import { LeadManagementService } from '../services/LeadManagementService';
import {
  MarketingLead,
  LeadType,
  LeadStatus,
  LEAD_STATUS_LABELS,
  LEAD_STATUS_COLORS,
  LEAD_TYPE_LABELS,
  getLeadDisplayName,
  getLeadCompany,
  toDate,
} from '../types/leads';
import { useAdminAuth } from '../contexts/AdminContext';

const SCORECARD_QUESTION_LABELS: Record<string, string> = {
  '1': 'Number of offices',
  '2': 'Commission tracking method',
  '3': 'Hours/month on reconciliation',
  '4': 'Leadership visibility into trail book',
  '5': 'Biggest commission challenge',
  '6': 'Approximate loan book size',
};

const ALL_STATUSES: LeadStatus[] = [
  'new',
  'contacted',
  'demo_booked',
  'demo_completed',
  'negotiating',
  'converted',
  'lost',
];

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
};

const LeadDetailPage: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const { user } = useAdminAuth();

  const [lead, setLead] = useState<MarketingLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState<LeadStatus | ''>('');
  const [statusNote, setStatusNote] = useState('');
  const [noteText, setNoteText] = useState('');
  const [showRawData, setShowRawData] = useState(false);
  const [showTechnical, setShowTechnical] = useState(false);
  const [updating, setUpdating] = useState(false);

  const leadType = type as LeadType;
  const docId = id || '';

  // Fetch lead on mount
  useEffect(() => {
    if (!type || !id) return;

    const fetchLead = async () => {
      setLoading(true);
      try {
        const data = await LeadManagementService.getLead(leadType, docId);
        setLead(data);
        if (data) {
          setNewStatus(data.status);
        }
      } catch (err) {
        console.error('Error fetching lead:', err);
        toast.error('Failed to load lead');
      } finally {
        setLoading(false);
      }
    };

    fetchLead();
  }, [type, id, leadType, docId]);

  // Re-fetch helper
  const refetchLead = async () => {
    const data = await LeadManagementService.getLead(leadType, docId);
    if (data) {
      setLead(data);
      setNewStatus(data.status);
    }
  };

  // Handle status update
  const handleUpdateStatus = async () => {
    if (!lead || !newStatus || newStatus === lead.status) return;
    setUpdating(true);
    try {
      await LeadManagementService.updateLeadStatus(
        leadType,
        docId,
        newStatus as LeadStatus,
        user?.email || 'admin',
        statusNote || undefined
      );
      toast.success(`Status updated to ${LEAD_STATUS_LABELS[newStatus as LeadStatus]}`);
      setStatusNote('');
      await refetchLead();
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
    } finally {
      setUpdating(false);
    }
  };

  // Handle booked demo toggle
  const handleToggleBookedDemo = async () => {
    if (!lead) return;
    setUpdating(true);
    try {
      await LeadManagementService.toggleBookedDemo(
        leadType,
        docId,
        !lead.bookedDemo,
        user?.email || 'admin'
      );
      toast.success(lead.bookedDemo ? 'Demo booking removed' : 'Demo marked as booked');
      await refetchLead();
    } catch (err) {
      console.error('Error toggling booked demo:', err);
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  // Handle converted toggle
  const handleToggleConverted = async () => {
    if (!lead) return;
    setUpdating(true);
    try {
      await LeadManagementService.toggleConverted(
        leadType,
        docId,
        !lead.converted,
        user?.email || 'admin'
      );
      toast.success(lead.converted ? 'Conversion removed' : 'Lead marked as converted');
      await refetchLead();
    } catch (err) {
      console.error('Error toggling converted:', err);
      toast.error('Failed to update');
    } finally {
      setUpdating(false);
    }
  };

  // Handle add note
  const handleAddNote = async () => {
    if (!lead || !noteText.trim()) return;
    setUpdating(true);
    try {
      await LeadManagementService.addNote(
        leadType,
        docId,
        noteText.trim(),
        user?.email || 'admin'
      );
      toast.success('Note added');
      setNoteText('');
      await refetchLead();
    } catch (err) {
      console.error('Error adding note:', err);
      toast.error('Failed to add note');
    } finally {
      setUpdating(false);
    }
  };

  // Format date for display
  const formatDate = (val: Date | { toDate: () => Date } | undefined): string => {
    const d = toDate(val);
    if (d.getTime() === 0) return '-';
    return d.toLocaleString('en-AU', {
      timeZone: 'Australia/Brisbane',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-400 text-sm">Loading lead...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!lead) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <h2 className="text-xl font-semibold text-white mb-2">Lead Not Found</h2>
          <p className="text-slate-400 mb-4">
            The lead you are looking for does not exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/leads')}
            className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg px-4 py-2 transition-colors"
          >
            Back to Leads
          </button>
        </div>
      </div>
    );
  }

  const displayName = getLeadDisplayName(lead);
  const companyName = getLeadCompany(lead);
  const source = lead.source;

  return (
    <div className="min-h-screen bg-slate-900 text-slate-200">
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">

        {/* Back Button */}
        <button
          onClick={() => navigate('/leads')}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          <span className="text-sm">Back to Leads</span>
        </button>

        {/* Header Section */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white">{displayName}</h1>
              {companyName !== '-' && (
                <p className="text-amber-100 mt-1">{companyName}</p>
              )}
              <p className="text-amber-200 text-sm mt-2">
                Created {formatDate(lead.timestamp)}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Lead type badge */}
              <span className="inline-flex items-center gap-1 bg-white/20 text-white text-xs font-medium px-3 py-1 rounded-full backdrop-blur-sm">
                <Tag className="w-3 h-3" />
                {LEAD_TYPE_LABELS[lead.type] || lead.type}
              </span>
              {/* Status badge */}
              <span
                className="inline-flex items-center text-xs font-medium px-3 py-1 rounded-full"
                style={{
                  backgroundColor: `${LEAD_STATUS_COLORS[lead.status]}33`,
                  color: LEAD_STATUS_COLORS[lead.status],
                }}
              >
                {LEAD_STATUS_LABELS[lead.status] || lead.status}
              </span>
              {/* Booked Demo badge */}
              {lead.bookedDemo && (
                <span className="inline-flex items-center gap-1 bg-cyan-500/20 text-cyan-300 text-xs font-medium px-3 py-1 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  Demo Booked
                </span>
              )}
              {/* Converted badge */}
              {lead.converted && (
                <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-300 text-xs font-medium px-3 py-1 rounded-full">
                  <CheckCircle2 className="w-3 h-3" />
                  Converted
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Two-column grid for Contact + Source */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Contact Info Card */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-amber-400" />
              Contact Information
            </h2>

            {/* Email */}
            <div className="space-y-1">
              <p className="text-sm text-slate-400">Email</p>
              <div className="flex items-center gap-2">
                <a
                  href={`mailto:${lead.email}`}
                  className="text-amber-400 hover:text-amber-300 transition-colors break-all"
                >
                  {lead.email}
                </a>
                <button
                  onClick={() => copyToClipboard(lead.email, 'Email')}
                  className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                  title="Copy email"
                >
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Phone */}
            {(lead.phone || lead.mobile) && (
              <div className="space-y-1">
                <p className="text-sm text-slate-400">Phone</p>
                <div className="flex items-center gap-2">
                  <a
                    href={`tel:${lead.phone || lead.mobile}`}
                    className="text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    {lead.phone || lead.mobile}
                  </a>
                  <button
                    onClick={() => copyToClipboard((lead.phone || lead.mobile)!, 'Phone')}
                    className="text-slate-500 hover:text-slate-300 transition-colors flex-shrink-0"
                    title="Copy phone"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {/* Company */}
            {companyName !== '-' && (
              <div className="space-y-1">
                <p className="text-sm text-slate-400">Company</p>
                <p className="text-slate-200 flex items-center gap-1">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  {companyName}
                </p>
              </div>
            )}

            {/* User Agent / IP (collapsible) */}
            {(lead.userAgent || lead.ipAddress) && (
              <div className="border-t border-slate-700 pt-3">
                <button
                  onClick={() => setShowTechnical(!showTechnical)}
                  className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 transition-colors"
                >
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${showTechnical ? 'rotate-180' : ''}`}
                  />
                  Technical Details
                </button>
                {showTechnical && (
                  <div className="mt-3 space-y-2">
                    {lead.userAgent && (
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">User Agent</p>
                        <p className="text-xs text-slate-400 break-all font-mono bg-slate-900 rounded p-2">
                          {lead.userAgent}
                        </p>
                      </div>
                    )}
                    {lead.ipAddress && (
                      <div className="space-y-1">
                        <p className="text-xs text-slate-500">IP Address</p>
                        <p className="text-xs text-slate-400 font-mono bg-slate-900 rounded p-2">
                          {lead.ipAddress}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Source Attribution Card */}
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-amber-400" />
              Source Attribution
            </h2>

            {source && (source.utm_source || source.utm_medium || source.utm_campaign || source.referralCode || source.landingPage || source.firstTouchSource) ? (
              <div className="space-y-3">
                {/* UTM fields */}
                <div className="grid grid-cols-2 gap-3">
                  {source.utm_source && (
                    <div className="space-y-1">
                      <p className="text-sm text-slate-400">UTM Source</p>
                      <p className="text-slate-200 text-sm">{source.utm_source}</p>
                    </div>
                  )}
                  {source.utm_medium && (
                    <div className="space-y-1">
                      <p className="text-sm text-slate-400">UTM Medium</p>
                      <p className="text-slate-200 text-sm">{source.utm_medium}</p>
                    </div>
                  )}
                  {source.utm_campaign && (
                    <div className="space-y-1">
                      <p className="text-sm text-slate-400">UTM Campaign</p>
                      <p className="text-slate-200 text-sm">{source.utm_campaign}</p>
                    </div>
                  )}
                  {source.utm_content && (
                    <div className="space-y-1">
                      <p className="text-sm text-slate-400">UTM Content</p>
                      <p className="text-slate-200 text-sm">{source.utm_content}</p>
                    </div>
                  )}
                  {source.utm_term && (
                    <div className="space-y-1">
                      <p className="text-sm text-slate-400">UTM Term</p>
                      <p className="text-slate-200 text-sm">{source.utm_term}</p>
                    </div>
                  )}
                </div>

                {/* Referral Code */}
                {source.referralCode && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Referral Code</p>
                    <span className="inline-flex items-center bg-amber-500/20 text-amber-300 text-sm font-medium px-3 py-1 rounded-full">
                      {source.referralCode}
                    </span>
                  </div>
                )}

                {/* URLs */}
                {source.landingPage && <SourceLink label="Landing Page" value={source.landingPage} />}
                {source.formPage && <SourceLink label="Form Page" value={source.formPage} />}
                {source.referrerUrl && <SourceLink label="Referrer URL" value={source.referrerUrl} />}

                {/* Touch points */}
                {source.firstTouchSource && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">First Touch Source</p>
                    <p className="text-slate-200 text-sm">
                      {source.firstTouchSource}
                      {source.firstTouchTimestamp && (
                        <span className="text-slate-500 ml-2 text-xs">
                          ({source.firstTouchTimestamp})
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {source.lastTouchSource && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-400">Last Touch Source</p>
                    <p className="text-slate-200 text-sm">{source.lastTouchSource}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-slate-500 text-sm italic">No source data</p>
            )}
          </div>
        </div>

        {/* Status Management Card */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-amber-400" />
            Status Management
          </h2>

          {/* Current status */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-400">Current Status:</span>
            <span
              className="inline-flex items-center text-sm font-semibold px-3 py-1 rounded-full"
              style={{
                backgroundColor: `${LEAD_STATUS_COLORS[lead.status]}33`,
                color: LEAD_STATUS_COLORS[lead.status],
              }}
            >
              {LEAD_STATUS_LABELS[lead.status]}
            </span>
          </div>

          {/* Status change controls */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Change Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value as LeadStatus)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              >
                <option value="">Select status...</option>
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {LEAD_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Note (optional)</label>
              <textarea
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Reason for status change..."
                rows={1}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
              />
            </div>
          </div>
          <button
            onClick={handleUpdateStatus}
            disabled={updating || !newStatus || newStatus === lead.status}
            className="bg-amber-500 hover:bg-amber-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          >
            {updating ? 'Updating...' : 'Update Status'}
          </button>

          {/* Toggle buttons */}
          <div className="border-t border-slate-700 pt-4 flex flex-wrap gap-3">
            <button
              onClick={handleToggleBookedDemo}
              disabled={updating}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                lead.bookedDemo
                  ? 'bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600'
              }`}
            >
              {lead.bookedDemo ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Booked Demo
            </button>
            <button
              onClick={handleToggleConverted}
              disabled={updating}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                lead.converted
                  ? 'bg-green-500/20 text-green-300 hover:bg-green-500/30 border border-green-500/40'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600 border border-slate-600'
              }`}
            >
              {lead.converted ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Converted
            </button>
          </div>
        </div>

        {/* Status Timeline */}
        {lead.statusHistory && lead.statusHistory.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-400" />
              Status Timeline
            </h2>
            <div className="relative ml-3">
              {/* Vertical line */}
              <div className="absolute left-[5px] top-2 bottom-2 w-0.5 bg-slate-700" />

              <div className="space-y-4">
                {[...lead.statusHistory]
                  .sort((a, b) => toDate(b.changedAt).getTime() - toDate(a.changedAt).getTime())
                  .map((entry, idx) => {
                    const toStatus = entry.to as LeadStatus;
                    const color = LEAD_STATUS_COLORS[toStatus] || '#6b7280';
                    return (
                      <div key={idx} className="relative pl-6">
                        {/* Colored dot */}
                        <div
                          className="absolute left-0 top-1.5 w-3 h-3 rounded-full border-2 border-slate-800"
                          style={{ backgroundColor: color }}
                        />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span
                              className="text-sm font-medium px-2 py-0.5 rounded-full"
                              style={{
                                backgroundColor: `${color}33`,
                                color: color,
                              }}
                            >
                              {LEAD_STATUS_LABELS[toStatus] || entry.to}
                            </span>
                            {entry.from && (
                              <span className="text-xs text-slate-500">
                                from {LEAD_STATUS_LABELS[entry.from as LeadStatus] || entry.from}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                            <span>by {entry.changedBy}</span>
                            <span>-</span>
                            <span>{formatDate(entry.changedAt)}</span>
                          </div>
                          {entry.note && (
                            <p className="text-sm text-slate-400 mt-1 italic">
                              &ldquo;{entry.note}&rdquo;
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>
        )}

        {/* Admin Notes */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-amber-400" />
            Admin Notes
          </h2>

          {lead.notes ? (
            <pre className="text-sm text-slate-300 whitespace-pre-wrap bg-slate-900 rounded-lg p-4 border border-slate-700 font-sans leading-relaxed">
              {lead.notes}
            </pre>
          ) : (
            <p className="text-slate-500 text-sm italic">No notes yet</p>
          )}

          <div className="space-y-2">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add a note..."
              rows={3}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
            />
            <button
              onClick={handleAddNote}
              disabled={updating || !noteText.trim()}
              className="bg-amber-500 hover:bg-amber-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors"
            >
              {updating ? 'Adding...' : 'Add Note'}
            </button>
          </div>
        </div>

        {/* Lead-Specific Data */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-amber-400" />
            {LEAD_TYPE_LABELS[lead.type] || lead.type} Data
          </h2>

          {/* Scorecard-specific */}
          {lead.type === 'scorecard' && (
            <div className="space-y-4">
              {/* Score gauge */}
              {lead.score !== undefined && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-400">Score</p>
                    <p className="text-lg font-bold text-white">{lead.score}/100</p>
                  </div>
                  <div className="w-full bg-slate-700 rounded-full h-3">
                    <div
                      className="h-3 rounded-full transition-all"
                      style={{
                        width: `${Math.min(100, Math.max(0, lead.score))}%`,
                        backgroundColor:
                          lead.score >= 70 ? '#22c55e' : lead.score >= 40 ? '#f59e0b' : '#ef4444',
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Tier badge */}
              {lead.tier && (
                <div className="space-y-1">
                  <p className="text-sm text-slate-400">Tier</p>
                  <span
                    className={`inline-flex items-center text-sm font-medium px-3 py-1 rounded-full ${
                      lead.tier === 'hot'
                        ? 'bg-red-500/20 text-red-300'
                        : lead.tier === 'warm'
                        ? 'bg-amber-500/20 text-amber-300'
                        : 'bg-blue-500/20 text-blue-300'
                    }`}
                  >
                    {lead.tier.charAt(0).toUpperCase() + lead.tier.slice(1)}
                  </span>
                </div>
              )}

              {/* Answers */}
              {lead.answers && Object.keys(lead.answers).length > 0 && (
                <div className="space-y-3 border-t border-slate-700 pt-4">
                  <p className="text-sm font-medium text-slate-300">Scorecard Answers</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(lead.answers).map(([key, value]) => (
                      <div
                        key={key}
                        className="bg-slate-900 rounded-lg p-3 border border-slate-700"
                      >
                        <p className="text-xs text-slate-500 mb-1">
                          {SCORECARD_QUESTION_LABELS[key] || `Question ${key}`}
                        </p>
                        <p className="text-sm text-slate-200">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Founders-specific */}
          {lead.type === 'founders' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Full Name', value: lead.fullName },
                { label: 'Email', value: lead.email },
                { label: 'Mobile', value: lead.mobile },
                { label: 'Company Name', value: lead.companyName },
                { label: 'Number of Offices', value: lead.numberOfOffices },
                { label: 'Number of Brokers', value: lead.numberOfBrokers },
                { label: 'Broker Type', value: lead.brokerType },
                { label: 'Annual Loan Volume', value: lead.annualLoanVolume },
                { label: 'Current Tracking Method', value: lead.currentTrackingMethod },
                { label: 'Biggest Challenge', value: lead.biggestChallenge },
                { label: 'Consent to Contact', value: lead.consentContact !== undefined ? (lead.consentContact ? 'Yes' : 'No') : undefined },
                { label: 'Subscribe to Updates', value: lead.subscribeUpdates !== undefined ? (lead.subscribeUpdates ? 'Yes' : 'No') : undefined },
              ]
                .filter((f) => f.value !== undefined && f.value !== null && f.value !== '')
                .map((field) => (
                  <div
                    key={field.label}
                    className="bg-slate-900 rounded-lg p-3 border border-slate-700"
                  >
                    <p className="text-xs text-slate-500 mb-1">{field.label}</p>
                    <p className="text-sm text-slate-200">{field.value}</p>
                  </div>
                ))}
            </div>
          )}

          {/* Contacts-specific */}
          {lead.type === 'contacts' && (
            <div className="space-y-3">
              {lead.name && (
                <div className="space-y-1">
                  <p className="text-sm text-slate-400">Name</p>
                  <p className="text-slate-200">{lead.name}</p>
                </div>
              )}
              {lead.message && (
                <div className="space-y-1">
                  <p className="text-sm text-slate-400">Message</p>
                  <div className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                    <p className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
                      {lead.message}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Waitlist — just basic info, no extra fields */}
          {lead.type === 'waitlist' && (
            <p className="text-slate-500 text-sm italic">
              Waitlist entry — no additional data fields.
            </p>
          )}
        </div>

        {/* Raw Data (collapsible) */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <button
            onClick={() => setShowRawData(!showRawData)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 transition-colors w-full"
          >
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showRawData ? 'rotate-180' : ''}`}
            />
            <span className="font-medium">Raw Data</span>
          </button>
          {showRawData && (
            <pre className="mt-4 text-xs text-slate-400 bg-slate-900 rounded-lg p-4 border border-slate-700 overflow-x-auto max-h-96 overflow-y-auto font-mono">
              {JSON.stringify(lead, null, 2)}
            </pre>
          )}
        </div>

      </div>
    </div>
  );
};

export default LeadDetailPage;
